import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import Button from '../ui/Button';

function AnimatedCounter({ target, duration = 4, suffix = '' }: { target: number; duration?: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let startTime: number | null = null;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  return <div ref={ref}>{count}{suffix}</div>;
}

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=1920)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/95 via-charcoal-900/80 to-charcoal-900/60" />
      </div>

      <div className="container-custom relative z-10 py-32 md:py-0">
        <div className="max-w-3xl">
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="heading-1 text-white mb-6 justify-center text-center"
          >
            Durable Building Solutions{' '}
            <span className="text-primary-400">That Stand the Test of Time</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-charcoal-300 mb-8 leading-relaxed justify-center text-center"
          >
            Expert construction and manufacturing precision for Jamaica's
            residential, commercial, and industrial projects. From foundation to
            final completion, we deliver excellence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
              <Button href="/contact" size="lg">
              Get a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <Button href="/services" variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10">
              View Our Services
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 flex items-center gap-8 pt-8 border-t border-white/10 justify-center text-center"
          >
            <div>
              <div className="text-3xl font-bold text-white">
                <AnimatedCounter target={10} suffix="+" />
              </div>
              <div className="text-sm text-charcoal-400">Years Experience</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">
                <AnimatedCounter target={50} suffix="+" />
              </div>
              <div className="text-sm text-charcoal-400">Projects Completed</div>
            </div>
            <div className="w-px h-12 bg-white/20 hidden sm:block" />
            <div className="hidden sm:block">
              <div className="text-3xl font-bold text-white">OSHA</div>
              <div className="text-sm text-charcoal-400">Safety Certified</div>
            </div>
            <div className="w-px h-12 bg-white/20 hidden sm:block" />
            <div className="hidden sm:block">
              <div className="text-3xl font-bold text-white">Islandwide</div>
              <div className="text-sm text-charcoal-400">Service Coverage</div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            delay: 0.8,
            repeat: Infinity,
            repeatType: 'reverse',
            repeatDelay: 0.5,
          }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </motion.div>
      </div>
    </section>
  );
}
